import type { IntlShape } from 'react-intl'
import {
	LoanEventType,
	LoanFinancingType,
	LoanInterestDaysMonth,
	LoanInterestDaysYear,
	LoanStatus,
} from '@viilu-web/shared/enums/loans'
import { exhaustiveMatchingGuard } from '@viilu-web/shared/util/exhaustiveMatchingGuard'

export const loanFinancingTypeMessage = (
	intl: IntlShape,
	loanFinancingType: LoanFinancingType,
) => {
	switch (loanFinancingType) {
		case LoanFinancingType.CAPITAL:
			return intl.formatMessage({
				defaultMessage: 'Pääomalaina',
				description: 'Label for loan financing type: capital loan',
			})
		case LoanFinancingType.MAINTENANCE:
			return intl.formatMessage({
				defaultMessage: 'Hoitolaina',
				description: 'Label for loan financing type: maintenance loan',
			})
		case LoanFinancingType.UNREALIZED_MAINTENANCE:
			return intl.formatMessage({
				defaultMessage: 'Pääomittamaton hoitolaina',
				description:
					'Label for loan financing type: unrealized maintenance loan',
			})
		default:
			return exhaustiveMatchingGuard(loanFinancingType, '')
	}
}

export const interestDaysMonthMessage = (
	intl: IntlShape,
	interestDaysMonth: LoanInterestDaysMonth,
) => {
	switch (interestDaysMonth) {
		case LoanInterestDaysMonth.THIRTY_DAYS:
			return intl.formatMessage({
				defaultMessage: '30 päivää',
				description: 'Label for interest days in month option 30 days.',
			})
		case LoanInterestDaysMonth.ACTUAL_OF_MONTH:
			return intl.formatMessage({
				defaultMessage: 'Kuukauden todelliset',
				description:
					'Label for interest days in month option actuals of the month.',
			})
		default:
			return exhaustiveMatchingGuard(interestDaysMonth, '')
	}
}

export const interestDaysYearMessage = (
	intl: IntlShape,
	interestDaysYear: LoanInterestDaysYear,
) => {
	switch (interestDaysYear) {
		case LoanInterestDaysYear.THREE_SIXTY:
			return intl.formatMessage({
				defaultMessage: '360 päivää',
				description: 'Label for interest days in year option 360 days.',
			})
		case LoanInterestDaysYear.THREE_SIXTY_FIVE:
			return intl.formatMessage({
				defaultMessage: '365 päivää',
				description: 'Label for interest days in year option 365 days.',
			})
		case LoanInterestDaysYear.ACTUAL_OF_YEAR:
			return intl.formatMessage({
				defaultMessage: 'Vuoden todelliset',
				description:
					'Label for interest days in year option actuals of the year.',
			})
		default:
			return exhaustiveMatchingGuard(interestDaysYear, '')
	}
}

export const loanStatusMessage = (intl: IntlShape, status: LoanStatus) => {
	switch (status) {
		case LoanStatus.DRAFT:
			return intl.formatMessage({
				defaultMessage: 'Luonnos',
				description: 'Label for loan status: draft',
			})
		case LoanStatus.ACTIVE:
			return intl.formatMessage({
				defaultMessage: 'Aktiivinen',
				description: 'Label for loan status: active',
			})
		case LoanStatus.ENDED:
			return intl.formatMessage({
				defaultMessage: 'Loppunut',
				description: 'Label for loan status: ended',
			})
		default:
			return exhaustiveMatchingGuard(status, '')
	}
}

export const loanEventTypeMessage = (
	intl: IntlShape,
	loanFinancingType: LoanEventType,
) => {
	switch (loanFinancingType) {
		case LoanEventType.LOAN_DRAW:
			return intl.formatMessage({
				defaultMessage: 'Lainan nosto',
				description: 'Label for loan event type: loan draw',
			})
		case LoanEventType.LOAN_BANK_REPAYMENT:
			return intl.formatMessage({
				defaultMessage: 'Lainalyhennys',
				description: 'Label for loan event type: loan repayment',
			})
		case LoanEventType.INTEREST_CHANGE:
			return intl.formatMessage({
				defaultMessage: 'Koron muutos',
				description: 'Label for loan event type: interest change',
			})
		case LoanEventType.SHAREGROUP_DEBT_REPAYMENT:
			return intl.formatMessage({
				defaultMessage: 'Osakkaan velkaosuus suoritus',
				description: 'Label for loan event type: sharegroup debt repayment',
			})
		case LoanEventType.COLLECTED_SHAREGROUP_BANK_REPAYMENT:
			return intl.formatMessage({
				defaultMessage: 'Lainalyhennys (osakkaiden velkaosuuksista)',
				description:
					'Label for loan event type: collected sharegroup debt repayment',
			})
		case LoanEventType.STARTING_BALANCE:
			return intl.formatMessage({
				defaultMessage: 'Alkutilanteen saldo',
				description: 'Label for loan event type: starting balance',
			})
		case LoanEventType.STARTING_COLLECTED_PAYMENTS:
			return intl.formatMessage({
				defaultMessage: 'Alkutilanteen kerätyt vastikkeet',
				description: 'Label for loan event type: starting collected payments',
			})
		case LoanEventType.STARTING_SURPLUS_OR_DEFICIT:
			return intl.formatMessage({
				defaultMessage: 'Alkutilanteen yli- tai alijäämä',
				description: 'Label for loan event type: starting surplus or deficit',
			})
		default:
			return exhaustiveMatchingGuard(loanFinancingType, '')
	}
}
